/**
 * Phase 2.4b — Déterministic translations for provider-entered static fields
 * (colors, fuel policies, …). We purposely avoid LLM calls for these
 * standardised values and ship a static FR⇄EN mapping instead.
 *
 * Usage:
 *   import { translateFuelPolicy, translateColor } from '@/i18n/translateStaticField';
 *   translateFuelPolicy(scooter.fuelPolicy, i18n.language)
 *   translateColor(scooter.color, i18n.language)
 *
 * Fallback: when the input value is not in the known dictionary, the raw
 * provider value is returned unchanged (never breaks the UI).
 */

const normalizeLang = (lang) => {
  if (!lang || typeof lang !== 'string') return 'fr';
  const short = lang.toLowerCase().split(/[-_]/)[0];
  return short === 'en' ? 'en' : 'fr';
};

const normalizeKey = (s) => (s || '').toString().trim().toLowerCase();

// Fuel policies — the three canonical values the provider dialog exposes.
const FUEL_POLICY_MAP = {
  'rendu plein':         { fr: 'Rendu plein',          en: 'Returned with full tank' },
  'rendu comme reçu':    { fr: 'Rendu comme reçu',     en: 'Returned as received' },
  'rendu comme recu':    { fr: 'Rendu comme reçu',     en: 'Returned as received' },
  'selon prix affiché':  { fr: 'Selon prix affiché',   en: 'Per displayed price' },
  'selon prix affiche':  { fr: 'Selon prix affiché',   en: 'Per displayed price' },
};

// Colors — most common FR colors a provider types in the "Couleur" free text.
// Keeps diacritics insensitive via normalizeKey (lower-cased).
const COLOR_MAP = {
  'blanc':      { fr: 'Blanc',      en: 'White' },
  'blanche':    { fr: 'Blanche',    en: 'White' },
  'noir':       { fr: 'Noir',       en: 'Black' },
  'noire':      { fr: 'Noire',      en: 'Black' },
  'rouge':      { fr: 'Rouge',      en: 'Red' },
  'bleu':       { fr: 'Bleu',       en: 'Blue' },
  'bleue':      { fr: 'Bleue',      en: 'Blue' },
  'vert':       { fr: 'Vert',       en: 'Green' },
  'verte':      { fr: 'Verte',      en: 'Green' },
  'jaune':      { fr: 'Jaune',      en: 'Yellow' },
  'gris':       { fr: 'Gris',       en: 'Gray' },
  'grise':      { fr: 'Grise',      en: 'Gray' },
  'argent':     { fr: 'Argent',     en: 'Silver' },
  'argenté':    { fr: 'Argenté',    en: 'Silver' },
  'orange':     { fr: 'Orange',     en: 'Orange' },
  'rose':       { fr: 'Rose',       en: 'Pink' },
  'violet':     { fr: 'Violet',     en: 'Purple' },
  'violette':   { fr: 'Violette',   en: 'Purple' },
  'marron':     { fr: 'Marron',     en: 'Brown' },
  'beige':      { fr: 'Beige',      en: 'Beige' },
  'doré':       { fr: 'Doré',       en: 'Gold' },
  'dore':       { fr: 'Doré',       en: 'Gold' },
  'noir mat':   { fr: 'Noir mat',   en: 'Matte black' },
  'bleu nuit':  { fr: 'Bleu nuit',  en: 'Midnight blue' },
  'bleu ciel':  { fr: 'Bleu ciel',  en: 'Sky blue' },
};

function pickFromMap(map, value, lang) {
  if (!value) return value || '';
  const entry = map[normalizeKey(value)];
  if (!entry) return value; // unknown → return raw provider input
  const short = normalizeLang(lang);
  return entry[short] || value;
}

export function translateFuelPolicy(value, lang) {
  return pickFromMap(FUEL_POLICY_MAP, value, lang);
}

export function translateColor(value, lang) {
  return pickFromMap(COLOR_MAP, value, lang);
}
